import type { ConsumableReplenishment, WasteReplacement } from '@ctcells/contract'
import { hasMessage, t } from '@ctcells/i18n'

import type { Column } from '../../atoms/DataTable.tsx'
import { dateTimeText, orDash } from '../format.ts'

/**
 * 관리 › 로그 「보충 기록」 · 「폐기통 교체」 탭의 열 — 응답에 있는 칸만(`ConsumableReplenishment` · `WasteReplacement`).
 *
 * 🔴 **`exclude_none` 이다** — 빈 칸은 `null` 이 아니라 **키째 없다**. 계약 좁히기 타입(`WithRequired`)은 있다고 말하지만
 *    실물에 없다(dev2 2026-10-12: 시약 행에 `count_after` 키 없음 · tc 행에 `reagent` 키 없음). 전부 `== null` 로 읽는다.
 * 🔴 **사람 칸은 user_id(UUID)** 다. `userNames`(`/users` · admin 한정)로 이름을 찾고, 못 찾으면(목록을 못 봤거나 지운 계정)
 *    **UUID 그대로** 둔다 — 「알 수 없음」으로 접지 않는다.
 * ⚠ 폐기통 교체의 `version` 은 원장 행 버전이라 사람이 읽을 값이 아니어서 열로 세우지 않는다.
 */

/** user_id → 사람이 읽는 이름. 없으면 UUID 원문 */
export function personText(
  userId: string | null | undefined,
  userNames: ReadonlyMap<string, string> | undefined,
): string {
  if (userId == null || userId === '') return t('mask.none')
  return userNames?.get(userId) ?? userId
}

function PersonCell({
  userId,
  userNames,
}: {
  userId: string | null | undefined
  userNames: ReadonlyMap<string, string> | undefined
}) {
  const text = personText(userId, userNames)
  // 이름을 못 찾아 UUID 가 그대로 서면 고정폭으로(식별자다)
  return userId != null && text === userId ? (
    <span className="mono" title={userId}>
      {text}
    </span>
  ) : (
    <span title={userId ?? undefined}>{text}</span>
  )
}

/** 보충 종류 — 계약 enum 그대로(`tip` · `tc` · `reagent`) */
export function replenishKindText(kind: ConsumableReplenishment['kind']): string {
  return t(`records.replenish.kind.${kind}`)
}

/**
 * 보충 후 — tip · tc 는 `count_after`(없으면 «모듈 만재값으로 리셋» · 계약 설명), 시약은 `reagent` 의 자리 · 웰 · 웰마다 양(µL).
 */
export function replenishAfterText(row: ConsumableReplenishment): string {
  if (row.kind === 'reagent') {
    const reagent = row.reagent
    if (reagent == null) return t('mask.none')
    return t('records.replenish.reagentAfter', {
      labware: reagent.labwareId,
      wells: reagent.wellNames.join(', '),
      volume: String(reagent.volume),
    })
  }
  return row.count_after == null
    ? t('records.replenish.resetFull')
    : t('records.replenish.countAfter', { count: String(row.count_after) })
}

/** 귀속 근거 — 계약 enum · 사전에 없으면 원문 */
export function attributionText(basis: string | null | undefined): string {
  if (basis == null || basis === '') return t('mask.none')
  const key = `records.attribution.${basis}`
  return hasMessage(key) ? t(key) : basis
}

export function replenishmentColumns(
  userNames: ReadonlyMap<string, string> | undefined,
): Column<ConsumableReplenishment>[] {
  return [
    {
      key: 'ts',
      header: t('column.time'),
      nowrap: true,
      sort: 'time',
      sortValue: (r) => r.replenished_at,
      cell: (r) => <span className="muted mono">{dateTimeText(r.replenished_at)}</span>,
    },
    {
      key: 'module',
      header: t('records.column.module'),
      nowrap: true,
      sort: 'text',
      sortValue: (r) => r.module_id,
      cell: (r) => <span className="mono">{r.module_id}</span>,
    },
    {
      key: 'kind',
      header: t('column.kind'),
      nowrap: true,
      sort: 'text',
      sortValue: (r) => replenishKindText(r.kind),
      cell: (r) => replenishKindText(r.kind),
    },
    {
      key: 'after',
      header: t('records.column.after'),
      sort: 'text',
      sortValue: (r) => replenishAfterText(r),
      cell: (r) => replenishAfterText(r),
    },
    {
      key: 'by',
      header: t('column.actor'),
      nowrap: true,
      sort: 'text',
      sortValue: (r) => personText(r.replenished_by, userNames),
      cell: (r) => <PersonCell userId={r.replenished_by} userNames={userNames} />,
    },
    {
      key: 'basis',
      header: t('column.attribution'),
      nowrap: true,
      sort: 'text',
      sortValue: (r) => attributionText(r.attribution_basis),
      cell: (r) => attributionText(r.attribution_basis),
    },
    {
      key: 'note',
      header: t('records.column.note'),
      sort: 'text',
      sortValue: (r) => r.note ?? '',
      cell: (r) => (
        <span className="muted nowrap" title={r.note ?? undefined}>
          {orDash(r.note)}
        </span>
      ),
    },
  ]
}

export function wasteReplacementColumns(
  userNames: ReadonlyMap<string, string> | undefined,
): Column<WasteReplacement>[] {
  return [
    {
      key: 'ts',
      header: t('column.time'),
      nowrap: true,
      sort: 'time',
      sortValue: (r) => r.replaced_at,
      cell: (r) => <span className="muted mono">{dateTimeText(r.replaced_at)}</span>,
    },
    {
      key: 'container',
      header: t('records.column.container'),
      nowrap: true,
      sort: 'text',
      sortValue: (r) => r.container_id,
      cell: (r) => <span className="mono">{r.container_id}</span>,
    },
    {
      key: 'by',
      header: t('column.actor'),
      nowrap: true,
      sort: 'text',
      sortValue: (r) => personText(r.replaced_by, userNames),
      cell: (r) => <PersonCell userId={r.replaced_by} userNames={userNames} />,
    },
    {
      key: 'note',
      header: t('records.column.note'),
      sort: 'text',
      sortValue: (r) => r.note ?? '',
      cell: (r) => (
        <span className="muted nowrap" title={r.note ?? undefined}>
          {orDash(r.note)}
        </span>
      ),
    },
  ]
}
